/* components/patterns/DocumentCard.jsx — карточка файла базы знаний (S9).
   // заменяет: frontend/components/documents/DocumentCard.jsx + S1-плитку DocumentCard.
   Иконка типа файла, мета, тип (Select), статус AI-резюме, само резюме (все состояния:
   pending/processing/done/failed/editing), блок двухуровневой памяти, действия.
   Данные/действия — извне (props), без импорта fakeApi. Резюмирование (processing→done)
   управляет родитель (DocumentsTab) таймером — карточка лишь рисует состояние.
   ХЕНДОФФ → frontend/components/documents/DocumentCard.jsx. */

import React from 'react';
import { Pill } from '../ui/Badge.jsx';
import { Button } from '../ui/Button.jsx';
import { Textarea } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Select } from '../ui/Select.jsx';
import { fmt } from '../../lib/format.js';

  const { useState, useEffect } = React;

  // тип файла по расширению / флагу is_image → иконка + короткая метка + цветовой ключ
  function fileMeta(doc) {
    const name = (doc.filename || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() : '';
    if (doc.is_image || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext))
      return { icon: 'image', tag: 'IMG', key: 'img' };
    if (ext === 'pdf') return { icon: 'file-text', tag: 'PDF', key: 'pdf' };
    if (['doc', 'docx', 'rtf'].includes(ext)) return { icon: 'file-text', tag: 'DOC', key: 'doc' };
    if (['xls', 'xlsx', 'csv'].includes(ext)) return { icon: 'grid', tag: 'XLS', key: 'xls' };
    if (['txt', 'md'].includes(ext)) return { icon: 'file-text', tag: 'TXT', key: 'txt' };
    return { icon: 'file', tag: 'ФАЙЛ', key: 'other' };
  }

  function fmtBytes(b) {
    if (b == null) return '—';
    if (b >= 1_000_000) return (b / 1_000_000).toFixed(1).replace('.', ',') + ' МБ';
    if (b >= 1000) return Math.round(b / 1000) + ' КБ';
    return b + ' Б';
  }

  const STATUS = {
    pending:    { tone: 'neutral', icon: 'clock',         label: 'Ожидает' },
    processing: { tone: 'info',    icon: 'refresh',        label: 'Обрабатывается' },
    done:       { tone: 'ok',      icon: 'check',          label: 'Готово' },
    failed:     { tone: 'bad',     icon: 'alert-circle',   label: 'Ошибка' },
  };

  function DocumentCard({
    doc, kindOptions = [], highlighted = false,
    onChangeKind, onDelete, onRegenerate, onRetry, onSaveSummary,
  }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(doc.summary || '');
    const [saving, setSaving] = useState(false);

    // если родитель сменил резюме (перегенерация) — синхронизируем черновик, выходим из правки
    useEffect(() => { if (!editing) setDraft(doc.summary || ''); /* eslint-disable-next-line */ }, [doc.summary]);

    const meta = fileMeta(doc);
    const st = STATUS[doc.summary_status] || STATUS.pending;

    const startEdit = () => { setDraft(doc.summary || ''); setEditing(true); };
    const cancelEdit = () => { setEditing(false); setDraft(doc.summary || ''); };
    const save = async () => {
      setSaving(true);
      await Promise.resolve(onSaveSummary && onSaveSummary(doc.id, draft.trim()));
      setSaving(false);
      setEditing(false);
    };

    return (
      <article className={'doccard' + (highlighted ? ' is-highlighted' : '')} data-status={doc.summary_status}>
        {/* шапка: иконка типа + имя + мета */}
        <header className="doccard__head">
          <span className={'doccard__filetype doccard__filetype--' + meta.key} aria-hidden="true">
            <Icon name={meta.icon} size={20} />
            <span className="doccard__filetype-tag">{meta.tag}</span>
          </span>
          <div className="doccard__head-main">
            <h3 className="doccard__name" title={doc.filename}>{doc.filename}</h3>
            <div className="doccard__meta t-caption">
              <span>{fmt.date(doc.created_at)}</span>
              <span className="doccard__dot" aria-hidden="true">·</span>
              <span className="num">{fmtBytes(doc.size_bytes)}</span>
            </div>
          </div>
        </header>

        {/* строка управления: тип в базе знаний + статус резюме */}
        <div className="doccard__control">
          <Select value={doc.kind} options={kindOptions} searchable={false}
            ariaLabel="Тип документа в базе знаний"
            className="doccard__kind"
            onChange={(v) => onChangeKind && onChangeKind(doc.id, v)} />
          <Pill tone={st.tone} icon={st.icon} size="sm">{st.label}</Pill>
        </div>

        {/* блок AI-резюме */}
        <div className="doccard__summary">
          {editing ? (
            <div className="doccard__edit">
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} minRows={4} maxRows={10}
                placeholder="Опишите содержимое документа для ассистента…" aria-label="Текст резюме" />
              <div className="doccard__edit-actions cluster cluster-6">
                <Button variant="primary" size="sm" loading={saving} onClick={save}>Сохранить</Button>
                <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>Отменить</Button>
              </div>
            </div>
          ) : doc.summary_status === 'pending' ? (
            <div className="doccard__pending">
              <div className="doccard__plate doccard__plate--pending">
                {/* summary_note — локальная пометка вкладки (резюме ждёт тарифа:
                    доступ к модели закрыт), иначе обычное ожидание */}
                <Icon name="clock" size={15} /> {doc.summary_note || 'Ожидает обработки'}
              </div>
              {/* Резюмирование стартует само только сразу после загрузки. Если
                  тогда доступ к модели был закрыт (или вкладку перезагрузили),
                  без этой кнопки документ «ожидал» бы вечно — и после оплаты. */}
              {onRetry && (
                <Button variant="secondary" size="sm" iconLeft="sparkles" onClick={() => onRetry(doc.id)}>
                  Сгенерировать резюме
                </Button>
              )}
            </div>
          ) : doc.summary_status === 'processing' ? (
            <div className="doccard__processing" aria-live="polite">
              <div className="doccard__processing-label t-caption">
                <Icon name="refresh" size={14} className="icon--spin" /> AI читает документ…
              </div>
              <div className="doccard__sk-lines">
                <span className="skeleton doccard__sk" style={{ width: '100%' }} />
                <span className="skeleton doccard__sk" style={{ width: '92%' }} />
                <span className="skeleton doccard__sk" style={{ width: '64%' }} />
              </div>
            </div>
          ) : doc.summary_status === 'failed' ? (
            <div className="doccard__fail">
              <p className="doccard__fail-msg t-body-sm">
                <Icon name="alert-circle" size={14} /> {doc.summary_error || 'Не удалось обработать документ.'}
              </p>
              <div className="cluster cluster-6">
                <Button variant="secondary" size="sm" iconLeft="refresh" onClick={() => onRetry && onRetry(doc.id)}>Повторить</Button>
                <Button variant="ghost" size="sm" iconLeft="edit" onClick={startEdit}>Описать вручную</Button>
              </div>
            </div>
          ) : (
            <p className="doccard__summary-text t-body-sm">{doc.summary || 'Резюме пустое.'}</p>
          )}
        </div>

        {/* двухуровневая память: что именно видит ассистент */}
        {doc.summary_status === 'done' && !editing && (
          <div className="doccard__memory" aria-label="Доступность для ассистента">
            <div className="doccard__mem-row" style={{ color: 'var(--status-ok-strong)' }}>
              <Icon name="check" size={13} /> Резюме — всегда в контексте
            </div>
            <div className="doccard__mem-row"
              style={{ color: doc.deep_readable ? 'var(--status-ok-strong)' : 'var(--text-mute)' }}>
              <Icon name={doc.deep_readable ? 'check' : 'minus'} size={13} />
              {doc.deep_readable
                ? 'Полный текст — подгружается по запросу'
                : 'Полный текст недоступен — формат не читается напрямую'}
            </div>
          </div>
        )}

        {/* подвал: действия */}
        <footer className="doccard__foot">
          <a className="doccard__open" href={doc.url || '#'} target="_blank" rel="noreferrer">
            <Icon name="external-link" size={15} /> Открыть файл
          </a>
          <div className="doccard__foot-actions">
            {doc.summary_status !== 'processing' && !editing && (
              <button type="button" className="doccard__act" onClick={startEdit}>
                <Icon name="edit" size={15} /> Изменить
              </button>
            )}
            <button type="button" className="doccard__act doccard__act--danger" onClick={() => onDelete && onDelete(doc)}>
              <Icon name="trash" size={15} /> Удалить
            </button>
          </div>
        </footer>
      </article>
    );
  }

export { DocumentCard };
export { fileMeta as docFileMeta };
export { fmtBytes };
